import React from 'react';
import { parseAsUtc } from '../../utils/dateUtils';

export const MEDICATION_SECTION_STYLES = {
  practice: 'border-emerald-200 bg-emerald-50/60 dark:border-emerald-800 dark:bg-emerald-900/20',
  insurance: 'border-indigo-200 bg-indigo-50/60 dark:border-indigo-800 dark:bg-indigo-900/20',
  unknown: 'border-amber-200 bg-amber-50/60 dark:border-amber-800 dark:bg-amber-900/20',
  outside: 'border-slate-300 bg-slate-50 dark:border-slate-600 dark:bg-slate-800/40',
  past: 'border-gray-200 bg-gray-50/80 dark:border-gray-600 dark:bg-gray-800/40',
} as const;

export type MedicationSectionTone = keyof typeof MEDICATION_SECTION_STYLES;

export function formatMedicationDate(value?: string | null): string | null {
  return value ? parseAsUtc(value).format('MMM D, YYYY') : null;
}

export function insuranceFillDateLabel(med: {
  insurance_history?: Array<{ last_fill_date?: string | null }> | null;
}): string | null {
  const dates = (med.insurance_history ?? [])
    .map((h) => h.last_fill_date)
    .filter((d): d is string => Boolean(d))
    .sort();
  return dates.length ? `Filled ${formatMedicationDate(dates[dates.length - 1])}` : null;
}

export function medicationStartDateLabel(
  med: { start_date?: string | null },
  verb: string,
): string | null {
  const d = formatMedicationDate(med.start_date);
  return d ? `${verb} ${d}` : null;
}

/** Tinted, counted group card — the shared medication-list section frame. */
export default function MedicationSectionCard({
  tone,
  title,
  subtitle,
  count,
  children,
}: {
  tone: MedicationSectionTone;
  title: string;
  subtitle?: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <section className={`rounded-lg border p-3 sm:p-4 ${MEDICATION_SECTION_STYLES[tone]}`}>
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <h4 className="text-md font-semibold text-gray-900 dark:text-white">{title}</h4>
        <span className="rounded-full bg-white/70 dark:bg-gray-800 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300">
          {count}
        </span>
      </div>
      {subtitle && <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">{subtitle}</p>}
      <div className="rounded-md bg-white/80 dark:bg-gray-800/80 px-2">
        {children}
      </div>
    </section>
  );
}
